<template>
  <div v-if="loaded" v-resize="() => debouncedHandler(resizeChart)" class="ga-chart-wrapper">
    <div :class="gaChartClasses">
      <h6>
        {{ $t('google-analytics') + ':' }}
        <span
          class="ga-last-update"
          v-html="
            $t('ga-last-update', {
              domain: this.domain,
              date: this.lastUpdateDate,
            })
          "
        ></span>
      </h6>
      <div ref="gaHighcharts" id="gaHighcharts" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import resize from 'vue-resize-directive';
import Highcharts from 'highcharts';
import { dayNames, monthNames, shortMonthNames } from '@/helpers/format-date-service';

export default {
  name: 'GoogleAnalyticsChart',
  directives: { resize },
  data() {
    return {
      chart: null,
      domain: '',
      lastUpdateDate: '',
      init: false,
      loaded: false,
      tooltips: [],
      chartConfigs: {},
      colors: ['#0074d9', '#5fca7a', '#ed7e16'],
    };
  },
  computed: {
    ...mapGetters(['getViewkeyView', 'getCurrentProject']),
    gaChartClasses() {
      return {
        'ga-chart': true,
        'ga-chart_viewkey': this.getViewkeyView,
      };
    },
  },
  watch: {
    getCurrentProject() {
      if (this.init) {
        this.fetchData();
      }
    },
  },
  created() {
    this.fetchData();
    this.init = true;
  },
  updated() {
    if (this.$refs.gaHighcharts) {
      this.chart = new Highcharts.chart('gaHighcharts', this.chartConfigs);
    }
  },
  methods: {
    debouncedHandler: _.debounce((fn) => fn(), 50),
    ...mapActions(['fetchAnalyticsChartData']),
    async fetchData() {
      try {
        this.loaded = false;
        const chartData = await this.fetchAnalyticsChartData(this.getCurrentProject);

        if (!chartData) return;

        let dataByDate = chartData?.attributes?.ga_data_by_date ?? {};
        this.domain = chartData?.attributes?.title ?? '';
        this.lastUpdateDate = chartData?.attributes?.last_update ?? '';
        this.generateTooltips(dataByDate);
        this.generateChartConfigs(dataByDate);
        this.loaded = true;
      } catch (error) {
        this.loaded = false;
      }
    },
    resizeChart() {
      if (this.chart) {
        this.chart.reflow();
      }
    },
    getTooltip(date, value, change) {
      if (!value) return '';

      return (
        `<b>${dayNames[date.getDay()]},` +
        ` ${shortMonthNames[date.getMonth()]} ${date.getDate()},` +
        ` ${date.getFullYear()}</b>` +
        `<br/>Organic Searches: ${value}` +
        (change !== false ? `<br/>Change: <b>${change.toFixed(2)}%</b>` : '')
      );
    },
    generateTooltips(dataByDate) {
      let lastMonth = false;
      this.tooltips = [];

      for (const month in dataByDate) {
        for (const day in dataByDate[month]) {
          let date = new Date(day);
          let index = date.getDate() - 1;
          let change = false;
          let lastDay = day.replace(month, lastMonth);

          if (lastMonth && dataByDate[lastMonth][lastDay]) {
            change = (100 * (dataByDate[lastMonth][lastDay] - dataByDate[month][day])) / dataByDate[month][day];
          }

          this.tooltips[index] =
            (this.tooltips[index] ? `${this.tooltips[index]}<br/>` : '') +
            this.getTooltip(date, dataByDate[month][day], change);
        }

        lastMonth = month;
      }
    },
    objIsNotEmpty(obj) {
      for (let key in obj) {
        if (obj[key] !== null && obj[key] != '') return true;
      }
      return false;
    },
    generateChartConfigs(dataByDate) {
      let series = [];
      let i = 0;
      let self = this;

      if (Object.keys(dataByDate).length) {
        for (const month in dataByDate) {
          // let monthName = monthNames[new Date(month).getMonth()];
          const temp = month.split('-');
          const monthIndex = temp.length ? +temp[1] - 1 : 0;

          let monthName = monthNames[monthIndex];
          let data = this.objIsNotEmpty(dataByDate[month]) ? Object.values(dataByDate[month]) : [];

          series.push({
            name: monthName,
            color: this.colors[i++],
            data: data,
          });
        }
      } else {
        let date = new Date();

        for (i = 2; i >= 0; i--) {
          let monthName = monthNames[date.getMonth()];

          series.unshift({
            name: monthName,
            color: this.colors[i],
            data: [],
          });

          date.setMonth(date.getMonth() - 1);
        }
      }

      this.chartConfigs = {
        chart: {
          events: [],
          height: 230,
          marginTop: 72,
          style: {
            fontFamily: 'Roboto',
          },
        },
        exporting: {
          enabled: false,
        },
        title: false,
        credits: false,
        xAxis: {
          type: 'category',
          allowDecimals: false,
        },
        yAxis: {
          allowDecimals: false,
          title: false,
        },
        legend: {
          align: 'right',
          verticalAlign: 'top',
          borderWidth: 0,
        },
        tooltip: {
          crosshairs: true,
          formatter: function () {
            return self.tooltips[this.points[0].point.index];
          },
          shared: true,
        },
        plotOptions: {
          series: {
            pointStart: 1,
          },
        },
        series: series,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.ga-chart-wrapper {
  width: 100%;
  padding-bottom: 9px;

  h6 {
    color: #909399;
    font-size: 14px;
    line-height: 14px;
    margin: 0;
    margin-bottom: 8px;
    padding-left: 24px;
    .ga-last-update {
      font-family: 'Roboto';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      align-items: center;
      color: #63697b;
    }
  }

  .ga-chart {
    border-radius: 4px;
    border: 1px solid #dee4eb;
    padding: 15px 16px;
    position: relative;

    &.ga-chart_viewkey {
      padding-top: 28px;
      background: white;
    }

    h6 {
      color: kw-color(kw-grey, 1);
      padding: 0 0 0 4px;
    }
  }
}

@media screen and (max-width: 959px) {
  .ga-chart-wrapper {
    border: none;

    .ga-chart {
      padding: 8px 16px 8px 8px;

      h6 {
        padding-left: 8px;
      }
    }
  }
}
</style>
